import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import "./MeetingRecordingPlayer.css";

function formatAudioTime(secondsValue) {
  const seconds = Number(secondsValue);
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

export default function MeetingRecordingPlayer({
  src,
  durationSeconds = 0,
  seekToSeconds = null,
  seekToken = 0,
  autoPlayOnSeek = true,
}) {
  const audioRef = useRef(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);

  useEffect(() => {
    setAudioCurrentTime(0);
    setAudioIsPlaying(false);
    setAudioDuration(Number(durationSeconds) || 0);
  }, [src, durationSeconds]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || seekToSeconds == null || seekToSeconds === "") return;
    const next = Number(seekToSeconds);
    if (!Number.isFinite(next) || next < 0) return;

    el.currentTime = next;
    setAudioCurrentTime(next);

    if (autoPlayOnSeek) {
      el.play().catch(() => {
        setAudioIsPlaying(false);
      });
    }
  }, [seekToSeconds, seekToken, autoPlayOnSeek]);

  const resolvedDuration = audioDuration > 0 ? audioDuration : Number(durationSeconds) || 0;
  const sliderMax = resolvedDuration > 0 ? resolvedDuration : 1;
  const progressPercent = sliderMax > 0
    ? (Math.min(audioCurrentTime, sliderMax) / sliderMax) * 100
    : 0;

  const toggleRecordingAudio = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        setAudioIsPlaying(true);
      } catch {
        setAudioIsPlaying(false);
      }
      return;
    }
    el.pause();
    setAudioIsPlaying(false);
  };

  const handleRecordingSeek = (event) => {
    const el = audioRef.current;
    if (!el) return;
    const nextTime = Number(event.target.value);
    if (!Number.isFinite(nextTime)) return;
    el.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
  };

  if (!src) {
    return <p className="mrp-status">Loading recording...</p>;
  }

  return (
    <>
      <audio
        ref={audioRef}
        className="mrp-audio-el"
        preload="auto"
        src={src}
        onLoadedMetadata={(e) => {
          const duration = Number(e.currentTarget.duration);
          if (Number.isFinite(duration) && duration > 0) {
            setAudioDuration(duration);
          }
        }}
        onTimeUpdate={(e) => setAudioCurrentTime(e.currentTarget.currentTime || 0)}
        onPlay={() => setAudioIsPlaying(true)}
        onPause={() => setAudioIsPlaying(false)}
        onEnded={() => setAudioIsPlaying(false)}
      >
        Your browser does not support audio playback.
      </audio>

      <div className="mrp-player">
        <button
          type="button"
          className="mrp-play-btn"
          onClick={toggleRecordingAudio}
          aria-label={audioIsPlaying ? "Pause recording" : "Play recording"}
        >
          {audioIsPlaying ? (
            <Pause className="mrp-play-icon" size={22} strokeWidth={2.8}  />
          ) : (
            <Play className="mrp-play-icon" size={22} strokeWidth={2.8} />
          )}
        </button>
        <input
          type="range"
          className="mrp-slider"
          min={0}
          max={sliderMax}
          step={0.1}
          value={Math.min(audioCurrentTime, sliderMax)}
          onChange={handleRecordingSeek}
          style={{ "--mrp-progress": `${progressPercent}%` }}
          aria-label="Recording progress"
        />
        <div className="mrp-time">
          {formatAudioTime(audioCurrentTime)} / {formatAudioTime(sliderMax)}
        </div>
      </div>
    </>
  );
}
